// ---------------------------------------------------------------------------
// exportCsv — build + download a CSV for a capability analysis (#6).
//
// buildCapabilityCsv / extractMeasurements / csvFilename are pure (unit-tested);
// downloadCsv performs the browser Blob download.
// ---------------------------------------------------------------------------

// RFC-4180 cell escaping (quote when the value contains a comma, quote, or newline)
// PLUS spreadsheet formula-injection neutralization (CSV injection / OWASP): a cell
// that leads with = + - @ (or tab / CR) is treated by Excel/Sheets/LibreOffice as a
// live formula, so a user-supplied field like a description of `=HYPERLINK(...)` or
// `=cmd|...` would execute on open. We prefix such cells with a single quote so they
// render as literal text — but ONLY when the cell is not a plain number, so legitimate
// numeric cells (a negative spec limit "-5.0000", a measurement -0.5) stay numeric and
// are never corrupted. csvCell is the single choke point for every exported cell.
function csvCell(v) {
    if (v === null || v === undefined) return '';
    let s = String(v);
    if (/^[=+\-@\t\r]/.test(s) && !Number.isFinite(Number(s))) s = `'${s}`;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Pull the individual measurements out of a dataset (fresh POST response OR a
// persisted history row, where arrays may arrive as JSON strings). Prefers the
// full individual-measurement array (allValues/all_values) over subgroup values.
export function extractMeasurements(dataset = {}) {
    let raw = dataset.allValues ?? dataset.all_values ?? dataset.values;
    if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch { raw = []; }
    }
    if (!Array.isArray(raw)) return [];
    return raw
        .flat()
        .filter((v) => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || v.trim() !== ''))
        .map(Number)
        .filter((v) => Number.isFinite(v));
}

// Build the CSV text: a labelled summary block (spec limits + indices) then the
// raw measurements. `generatedAt` is passed in (not read from the clock) so the
// function stays pure/deterministic for tests.
export function buildCapabilityCsv(dataset = {}, measurements = [], generatedAt) {
    const rows = [];
    rows.push(['Capability Cockpit — Analysis Export']);
    if (dataset.description) rows.push(['Description', dataset.description]);
    if (generatedAt) rows.push(['Generated', generatedAt]);

    const num = (label, v, dp) => {
        const n = Number(v);
        if (Number.isFinite(n)) rows.push([label, n.toFixed(dp)]);
    };
    num('Spec LSL', dataset.lsl, 4);
    num('Spec USL', dataset.usl, 4);
    rows.push(['Sample size (n)', measurements.length]);
    num('Mean', dataset.mean, 6);
    num('StdDev (overall)', dataset.std_dev, 6);
    num('StdDev (within)', dataset.within_std_dev, 6);
    num('Cp', dataset.cp, 4);
    num('Cpk', dataset.cpk, 4);
    num('Cpu', dataset.cpu, 4);
    num('Cpl', dataset.cpl, 4);
    // M6: pp/ppk are the classical normal-assumption indices regardless of fit — label
    // "(normal)" so a non-normally-fit study's value isn't read as its actual capability.
    num('Pp (normal)', dataset.pp, 4);
    num('Ppk (normal)', dataset.ppk, 4);
    num('Ppu', dataset.ppu, 4);
    num('Ppl', dataset.ppl, 4);
    num('PPM (within)', dataset.ppm_within, 2);
    num('PPM (overall)', dataset.ppm_overall, 2);

    rows.push([]); // blank separator
    rows.push(['Measurement', 'Value']);
    measurements.forEach((v, i) => rows.push([i + 1, v]));

    return rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

// Safe, descriptive filename from the dataset.
export function csvFilename(dataset = {}) {
    const base = String(dataset.description || 'capability')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'capability';
    const id = dataset.id ? `-${dataset.id}` : '';
    return `${base}${id}.csv`;
}

// Trigger a browser download of the given CSV text.
export function downloadCsv(filename, text) {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Convenience: build + download for a dataset in one call.
export function exportDatasetCsv(dataset) {
    const measurements = extractMeasurements(dataset);
    const csv = buildCapabilityCsv(dataset, measurements, new Date().toISOString());
    downloadCsv(csvFilename(dataset), csv);
}
